import {
  Children,
  Fragment,
  createContext,
  cloneElement,
  isValidElement,
  useContext,
  useEffect,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import schema from "./template-pages-schema.json";
import { getAppTemplate, IS_DEMO, type CustomNavigation } from "./api";
export type { TemplatePageConfig } from "./template-pages.mjs";
import {
  normalizeTemplatePages,
  normalizeTemplateTheme,
  type TemplatePageConfig,
} from "./template-pages.mjs";
import {
  normalizeComponents,
  normalizeBottomMenus,
  type AppComponent,
  type BottomMenu,
} from "./template-platform.mjs";
export type AppTemplateConfig = {
  id: string;
  code: string;
  rendererVersion: number;
  theme: { accent: string; surface: string };
  pages: TemplatePageConfig[];
  components: AppComponent[];
  bottomMenu: BottomMenu | null;
};
export function normalizeTemplate(value: unknown): AppTemplateConfig {
  const raw = value as Partial<AppTemplateConfig> | null;
  const pages = normalizeTemplatePages(
    raw?.pages,
    Number(raw?.rendererVersion) >= 2,
  );
  return {
    id: raw?.id || "TPL-DEFAULT",
    code: raw?.code || "default",
    rendererVersion: 2,
    theme: normalizeTemplateTheme(raw?.theme),
    pages,
    components: normalizeComponents(raw?.components),
    bottomMenu:
      raw?.bottomMenu === null
        ? null
        : raw?.bottomMenu
          ? normalizeBottomMenus([raw.bottomMenu], [])[0]
          : normalizeBottomMenus(undefined, [
              {
                id: "TPL-DEFAULT",
                name: "默认模板",
                bottomMenuId: "BM-DEFAULT",
                pages,
              },
            ])[0],
  };
}
export const defaultTemplate = normalizeTemplate(null);
export function useTemplateConfig() {
  const [preview] = useState(() => {
    try {
      const hash = location.hash.slice(1);
      const data = new URLSearchParams(
        hash.startsWith("layoutPreview=") ? hash : hash.split("?")[1],
      ).get("layoutPreview");
      return data && data.length < 40000 ? JSON.parse(data) : null;
    } catch {
      return null;
    }
  });
  const [template, setTemplate] = useState<AppTemplateConfig>(() =>
    normalizeTemplate(preview),
  );
  const [navigation, setNavigation] = useState<CustomNavigation | null>(null);
  const [error, setError] = useState("");
  const [ready, setReady] = useState(IS_DEMO);
  useEffect(() => {
    if (IS_DEMO) return;
    const controller = new AbortController();
    getAppTemplate(controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        if (!result.template) throw new Error("模板不存在或已停用");
        const config = normalizeTemplate({
          ...(result.template as object),
          components: result.components,
          bottomMenu: result.bottomMenu,
        });
        setTemplate(
          preview
            ? { ...config, ...normalizeTemplate({ ...config, ...preview }) }
            : config,
        );
        const groups = Array.isArray(result.items) ? result.items : [];
        setNavigation({
          id: config.id,
          name: groups.length === 1 ? groups[0].name : "模板导航",
          displayTitle:
            groups.length === 1 && typeof groups[0].displayTitle === "string"
              ? groups[0].displayTitle.trim()
              : "",
          items: groups.flatMap((group) =>
            Array.isArray(group.items)
              ? group.items.map((item) => ({
                  ...item,
                  id: `${group.id}:${item.id}`,
                  categories: Array.isArray(item.categories)
                    ? item.categories
                    : [],
                  keywords: Array.isArray(item.keywords) ? item.keywords : [],
                  icon: typeof item.icon === "string" ? item.icon : "",
                }))
              : [],
          ),
        });
      })
      .catch((e) => {
        if (!controller.signal.aborted)
          setError(e instanceof Error ? e.message : "模板读取失败");
      })
      .finally(() => {
        if (!controller.signal.aborted) setReady(true);
      });
    return () => controller.abort();
  }, [preview]);
  return { template, navigation, error, ready, preview: !!preview };
}
const TemplateContext = createContext(defaultTemplate);
export const TemplateProvider = TemplateContext.Provider;
const RuntimeContext = createContext<{
  goToUrl: (url: string) => void;
  videoGrid: () => ReactNode;
}>({ goToUrl: () => {}, videoGrid: () => null });
export const TemplateRuntimeProvider = RuntimeContext.Provider;
function ComponentContent({ component }: { component: AppComponent }) {
  const runtime = useContext(RuntimeContext),
    p = component.params;
  const style = {
    padding: p.padding,
    borderRadius: p.radius,
    color: p.color || undefined,
    background: p.background || undefined,
    "--component-gap": `${p.gap}px`,
    "--component-columns": p.columns,
  } as CSSProperties;
  const copy = (
    <>
      {p.title && <h2>{p.title}</h2>}
      {p.text && <p>{p.text}</p>}
    </>
  );
  const image = p.imageUrl ? (
    <img src={p.imageUrl} alt={p.title || component.name} />
  ) : null;
  let content: ReactNode = copy;
  if (component.type === "banner")
    content = (
      <>
        {image}
        <div className="component-banner-copy">{copy}</div>
      </>
    );
  if (component.type === "image")
    content = (
      <>
        {image}
        {copy}
      </>
    );
  if (component.type === "video-grid")
    content = (
      <>
        {p.url ? (
          <button type="button" onClick={() => runtime.goToUrl(p.url)}>
            {copy}
          </button>
        ) : (
          copy
        )}
        {runtime.videoGrid()}
      </>
    );
  return (
    <div
      className={`configured-component component-${component.type}`}
      data-component={component.id}
      style={style}
    >
      {p.url && component.type !== "video-grid" ? (
        <button type="button" onClick={() => runtime.goToUrl(p.url)}>
          {content}
        </button>
      ) : (
        content
      )}
    </div>
  );
}
function styledSlot(slot: ReactNode, component: AppComponent) {
  const p = component.params;
  if (!p.padding && !p.gap && !p.radius && !p.color && !p.background)
    return slot;
  const style = {
    padding: p.padding || undefined,
    gap: p.gap || undefined,
    borderRadius: p.radius || undefined,
    color: p.color || undefined,
    background: p.background || undefined,
  };
  if (
    isValidElement<{ children?: ReactNode }>(slot) &&
    isValidElement<{ style?: CSSProperties }>(slot.props.children)
  ) {
    const child = slot.props.children;
    return cloneElement(slot, {
      children: cloneElement(child, {
        style: { ...child.props.style, ...style },
      }),
    });
  }
  return <div style={style}>{slot}</div>;
}
export function TemplateBlock({
  children,
}: {
  id: string;
  children: ReactNode;
}) {
  return <>{children}</>;
}
type LayoutProps = {
  page: TemplatePageConfig;
  className: string;
  children: ReactNode;
};
function StackLayout({ page, className, children }: LayoutProps) {
  const runtime = useContext(RuntimeContext);
  return (
    <section
      className={`${className} template-page template-${page.layout}`}
      data-template-page={page.id}
      data-template-layout={page.layout}
      data-hidden-blocks={
        page.blocks
          .filter((block) => !block.enabled)
          .map((block) => block.id)
          .join(" ") || undefined
      }
      aria-label={page.title}
      style={
        {
          "--template-columns": page.columns,
          "--template-gap": `${page.gap}px`,
        } as CSSProperties
      }
    >
      {page.kind === "custom" && (
        <header className="simple-header">
          <button
            type="button"
            aria-label="返回首页"
            onClick={() => runtime.goToUrl("#home")}
          >
            ‹
          </button>
          <h1>{page.title}</h1>
        </header>
      )}
      {children}
    </section>
  );
}
// Layout registry holds code-owned renderers. Configuration only chooses a
// known renderer and known blocks, never executes supplied HTML or scripts.
const renderers: Record<string, typeof StackLayout> = Object.fromEntries(
  schema.pages.flatMap((page) =>
    page.layouts.map((layout) => [layout, StackLayout]),
  ),
);
export function TemplatePage({
  id,
  className,
  children,
  forceFeed = false,
}: {
  id: string;
  className: string;
  children: ReactNode;
  forceFeed?: boolean;
}) {
  const template = useContext(TemplateContext);
  const configured =
    template.pages.find((p) => p.id === id) ||
    defaultTemplate.pages.find((p) => p.id === id)!;
  const page =
    forceFeed && id === "home"
      ? { ...configured, layout: "video-feed" }
      : configured;
  const slots = new Map<string, ReactNode>();
  Children.forEach(children, (child) => {
    if (isValidElement<{ id?: string }>(child) && child.props.id)
      slots.set(child.props.id, child);
  });
  const Renderer = renderers[page.layout] || StackLayout;
  const renderBlock = (block: TemplatePageConfig["blocks"][number]) => {
    const component = template.components.find(
      (c) => c.id === block.componentId && c.status === "启用",
    );
    if (!component) return null;
    return component.type === "builtin" ? (
      styledSlot(slots.get(block.id), component)
    ) : (
      <ComponentContent component={component} />
    );
  };
  const isCustom = (block: TemplatePageConfig["blocks"][number]) =>
    template.components.find((c) => c.id === block.componentId)?.type !==
    "builtin";
  return (
    <Renderer page={page} className={className}>
      {page.blocks
        .filter(
          (block) =>
            block.enabled &&
            (id !== "home" ||
              (page.layout === "video-grid"
                ? ["header", "sorting", "catalog"].includes(block.id) ||
                  isCustom(block)
                : block.id !== "catalog")),
        )
        .map((block) => (
          <Fragment key={block.id}>
            {id === "home" && page.layout === "video-feed" && isCustom(block)
              ? null
              : renderBlock(block)}
          </Fragment>
        ))}
      {id === "home" && page.layout === "video-feed" && (
        <div className="feed-component-overlay">
          {page.blocks
            .filter((b) => b.enabled && isCustom(b))
            .map((b) => (
              <Fragment key={b.id}>{renderBlock(b)}</Fragment>
            ))}
        </div>
      )}
    </Renderer>
  );
}
export function TemplateFeedBlocks({ children }: { children: ReactNode }) {
  const template = useContext(TemplateContext);
  const page = template.pages.find((p) => p.id === "home")!;
  const slots = new Map<string, ReactNode>();
  Children.forEach(children, (child) => {
    if (isValidElement<{ id?: string }>(child) && child.props.id)
      slots.set(child.props.id, child);
  });
  return (
    <>
      {page.blocks
        .filter(
          (block) =>
            block.enabled &&
            template.components.find((c) => c.id === block.componentId)
              ?.type === "builtin",
        )
        .map((block) => (
          <Fragment key={block.id}>
            {styledSlot(
              slots.get(block.id),
              template.components.find((c) => c.id === block.componentId)!,
            )}
          </Fragment>
        ))}
    </>
  );
}
